import React, { useCallback, useEffect, useMemo } from 'react'
import { Grid, makeStyles, useTheme } from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import PropTypes from 'prop-types'
import Text from '../../../atoms/Text'
import Avatar from '../../../atoms/Avatar'
import { CUSTODIANS, TEXT_VARIANTS } from '../../../../constants'
import { useBoolean, useCheckPolicy } from '../../../../hooks'
import { postNamedCommand } from '../../../../service'
import TextInput from '../../../molecules/TextInput'
import { messageToUse } from '../common'
import { ACCOUNTS } from '../../../../policies/admin'
import { ACCOUNT_FORM_NAMES } from './helpers'
import EditButton from './EditButton'
import SubmitButtons from './SubmitButtons'

const useStyles = makeStyles(() => ({
  container: {
    width: '100%',
    position: 'relative'
  },
  header: {
    flexDirection: 'column'
  },
  divider: {
    margin: '28px 0px'
  },
  nameContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    maxWidth: '550px',
    width: '100%'
  },
  custodianContainer: {
    display: 'flex',
    justifyContent: 'space-around'
  },
  datesContainer: {
    marginBottom: '30px'
  },
  title: {
    color: '#212945',
    lineHeight: '2.5625rem',
    fontSize: '30px'
  },
  subTitle: {
    fontWeight: 350,
    lineHeight: '2rem'
  },
  detailsContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '1.5rem'
  },
  detailsTitle: {
    color: '#141929',
    lineHeight: 'normal',
    fontSize: '0.875rem',
    marginBottom: '1rem',
    fontWeight: 350
  },
  detailsSubTitle: {
    color: '#141929',
    fontSize: '1rem'
  },
  custodianContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '10px'
  },
  avatar: {
    marginRight: '0.5rem'
  }
}))

export const mapFormDefaultValues = (account) => {
  if (!account) return null

  const extraFields = {}

  if (account.dataSourceId === CUSTODIANS.BY_ALL_ACCOUNTS) {
    extraFields[ACCOUNT_FORM_NAMES.accountDisplayNumber] = account.displayNumber
  }

  return {
    ...extraFields,
    [ACCOUNT_FORM_NAMES.accountId]: account.accountId,
    [ACCOUNT_FORM_NAMES.accountLongName]: account.longName,
    [ACCOUNT_FORM_NAMES.accountDisplayName]: account.displayName || account.longName,
    [ACCOUNT_FORM_NAMES.accountLongNumber]: account.longNumber,
    [ACCOUNT_FORM_NAMES.closedDate]: account?.closeDate || account.startDate
  }
}

const AccountDetailsHeader = ({
  account,
  marketValue,
  onSave,
  disableEdit,
  setDisableEdit
}) => {
  const theme = useTheme()
  const [editMode, setEditMode] = useBoolean()
  const [formLoading, setFormLoading] = useBoolean()
  const classes = useStyles({ editMode })
  const canEditDisplayName = useCheckPolicy(ACCOUNTS.editDisplayName)

  const defaultValues = useMemo(() => {
    return mapFormDefaultValues(account)
  }, [account])

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {}
  })

  const {
    reset,
    register,
    handleSubmit,
    control,
    formState: { errors, isValid }
  } = formMethods

  useEffect(() => {
    reset(defaultValues)
  }, [reset, defaultValues])

  const custodian = useMemo(() => {
    const custodian = account?.custodian ? account.custodian[0] : {}
    return {
      name: custodian?.name || '',
      imageUrl: custodian?.imageUrl
    }
  }, [account])

  const onSubmit = useCallback(
    async (values) => {
      try {
        setFormLoading.on()
        await postNamedCommand('accounts', 'updateAccountDisplayName', {
          displayName: values.accountDisplayName?.trim() || '',
          accountId: account.accountId
        })
        onSave()
      } catch (err) {
        console.error(err)
      } finally {
        setDisableEdit.off()
        setEditMode.off()
        setFormLoading.off()
      }
    },
    [
      setFormLoading,
      setEditMode,
      onSave,
      account.accountId,
      setDisableEdit
    ]
  )

  const onResetForm = useCallback(() => {
    reset(mapFormDefaultValues(account))
  }, [account, reset])

  const onCancel = useCallback(() => {
    onResetForm()
    setEditMode.off()
    setDisableEdit.off()
  }, [onResetForm, setEditMode, setDisableEdit])

  const handleEditMode = useCallback(() => {
    setEditMode.toggle()
    setDisableEdit.toggle()
  }, [setEditMode, setDisableEdit])

  const renderDetailsBlock = useCallback(
    (title, subtitle) => {
      return (
        <div className={classes.detailsContainer}>
          <Text
            text={title}
            className={classes.detailsTitle}
          />
          <Text
            text={subtitle}
            className={classes.detailsSubTitle}
          />
        </div>
      )
    },
    [
      classes.detailsTitle,
      classes.detailsSubTitle,
      classes.detailsContainer
    ]
  )

  return (
    <div className={classes.container}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container>
          <Grid container item xs={12} spacing={4}>
            <Grid container item xs={12}>
              <div className={classes.nameContainer}>
                {editMode && canEditDisplayName
                  ? (
                    <>
                      <Controller
                        name={ACCOUNT_FORM_NAMES.accountDisplayName}
                        render={() => (
                          <TextInput
                            fullWidth
                            name={ACCOUNT_FORM_NAMES.accountDisplayName}
                            placeholder='Display Name'
                            helperText='80 max characters'
                            error={Boolean(errors[ACCOUNT_FORM_NAMES.accountDisplayName])}
                            {...register(ACCOUNT_FORM_NAMES.accountDisplayName, { required: true, minLength: 1, maxLength: 80 })}
                          />)}
                        control={control}
                      />
                      {Boolean(errors[ACCOUNT_FORM_NAMES.accountDisplayName]) && (
                        <Text
                          text={messageToUse(errors[ACCOUNT_FORM_NAMES.accountDisplayName])}
                          color={theme.palette.error.main}
                          className={classes.confirmError}
                        />
                      )}
                    </>
                  )
                  : (
                    <Text
                      text={account?.displayName || account?.longName}
                      className={classes.title}
                      variant={TEXT_VARIANTS.h1}
                    />
                  )}
                {canEditDisplayName && (
                  <EditButton
                    onClick={handleEditMode}
                    editMode={editMode}
                    disabled={!editMode && disableEdit}
                  />
                )}
              </div>
            </Grid>
          </Grid>
          <Grid container item xs={12} spacing={4}>
            <Grid container item xs={6} className={classes.header}>
              <Text
                text={account?.longNumber}
                className={classes.subTitle}
              />
            </Grid>
            <Grid container item xs={12} sm={6} className={classes.custodianContainer}>
              <div className={classes.detailsContainer}>
                <Text
                  text='Custodian'
                  className={classes.detailsTitle}
                />
                <div className={classes.custodianContent}>
                  <Avatar
                    customSize='2rem'
                    src={custodian.imageUrl}
                    customClassName={classes.avatar}
                    isPublicResource
                    avatarLetters={custodian.name}
                  />
                  <Text
                    text={custodian.name}
                    className={classes.detailsSubTitle}
                  />
                </div>
              </div>
              {renderDetailsBlock('Market Value', marketValue)}
            </Grid>
          </Grid>
        </Grid>
        <SubmitButtons
          editMode={editMode}
          onCancel={onCancel}
          isSubmitting={formLoading}
          isFormValid={isValid}
        />
      </form>
    </div>
  )
}

AccountDetailsHeader.propTypes = {
  account: PropTypes.object,
  marketValue: PropTypes.string,
  onSave: PropTypes.func,
  disableEdit: PropTypes.bool,
  setDisableEdit: PropTypes.object
}

export default AccountDetailsHeader
